import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider } from 'notistack';
import * as Sentry from '@sentry/react';
import { isAxiosError } from 'axios';
import { ThemeModeProvider } from './ThemeModeContext';
import { useAuthStore } from './store/authStore';
import { isTokenExpired } from './utils/tokenUtils';
import { doRefresh } from './services/api';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { OfflineBanner } from './components/common/OfflineBanner';
import { useRateLimitHandler } from './hooks/useRateLimitHandler';
import MainLayout from './components/Layout/MainLayout';
import RoleAccessGate from './components/common/RoleAccessGate';

// Auth pages (kept eager — first screens a guest sees)
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import VerifyPhonePage from './pages/auth/VerifyPhonePage';
import ResendVerificationPage from './pages/auth/ResendVerificationPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';

// Route-level code splitting: all app pages are lazy-loaded
const DashboardPage = lazy(() => import('./pages/dashboard/DashboardPage'));
const CampaignsPage = lazy(() => import('./pages/campaigns/CampaignsPage'));
const CreateCampaignPage = lazy(() => import('./pages/campaigns/CreateCampaignPage'));
const EditCampaignPage = lazy(() => import('./pages/campaigns/EditCampaignPage'));
const CampaignDetailPage = lazy(() => import('./pages/campaigns/CampaignDetailPage'));
const AddScreensPage = lazy(() => import('./pages/campaigns/AddScreensPage'));
const CampaignCreativeUploadPage = lazy(() => import('./pages/campaigns/CampaignCreativeUploadPage'));
const ScreensPage = lazy(() => import('./pages/screens/ScreensPage'));
const MoneyPage = lazy(() => import('./pages/payouts/MoneyPage'));
const CreateScreenPage = lazy(() => import('./pages/screens/CreateScreenPage'));
const UpdateScreenPage = lazy(() => import('./pages/screens/UpdateScreenPage'));
const ScreenDetailPage = lazy(() => import('./pages/screens/ScreenDetailPage'));
const DiscoverScreensPage = lazy(() => import('./pages/screens/DiscoverScreensPage'));
const BookingsPage = lazy(() => import('./pages/bookings/BookingsPage'));
const LegacyBookingRedirect = lazy(() => import('./components/bookings/LegacyBookingRedirect'));
const BookingDetailPage = lazy(() => import('./pages/bookings/BookingDetailPage'));
const BookingReceiptPage = lazy(() => import('./pages/bookings/BookingReceiptPage'));
const AnalyticsPage = lazy(() => import('./pages/analytics/AnalyticsPage'));
const AdvertiserReportPage = lazy(() => import('./pages/reports/AdvertiserReportPage'));
const CampaignReportPage = lazy(() => import('./pages/reports/CampaignReportPage'));
const PayoutsPage = lazy(() => import('./pages/payouts/PayoutsPage'));
const WalletPage = lazy(() => import('./pages/wallet/WalletPage'));
const NotificationsPage = lazy(() => import('./pages/notifications/NotificationsPage'));
const DemoPage = lazy(() => import('./pages/demo/DemoPage'));
const ExploreScreensPage = lazy(() => import('./pages/public/ExploreScreensPage'));
const LandingPage = lazy(() => import('./pages/public/LandingPage'));
const AboutUs = lazy(() => import('./pages/legal/AboutUs'));
const ContactUs = lazy(() => import('./pages/legal/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/legal/PrivacyPolicy'));
const RefundPolicy = lazy(() => import('./pages/legal/RefundPolicy'));
const TermsOfService = lazy(() => import('./pages/legal/TermsOfService'));
const CookiesPolicy = lazy(() => import('./pages/legal/CookiesPolicy'));
const ContentPolicy = lazy(() => import('./pages/legal/ContentPolicy'));
const CommunityGuidelines = lazy(() => import('./pages/legal/CommunityGuidelines'));
const Disclaimer = lazy(() => import('./pages/legal/Disclaimer'));
const ProfileSettingsPage = lazy(() => import('./pages/profile/ProfileSettingsPage'));
const AdminPayoutsPage = lazy(() => import('./pages/payouts/AdminPayoutsPage'));
const LogsPage = lazy(() => import('./pages/playlogs/LogsPage'));
const MonitorRoomPage = lazy(() => import('./pages/monitor/MonitorRoomPage'));
const AdminMachinesPage = lazy(() => import('./pages/admin/AdminMachinesPage'));
const AdminVerificationsPage = lazy(() => import('./pages/admin/AdminVerificationsPage'));
const AdminVisibilityRequestsPage = lazy(() => import('./pages/admin/AdminVisibilityRequestsPage'));
const AdminVisionRequestsPage = lazy(() => import('./pages/admin/AdminVisionRequestsPage'));
const AdminCameraFleetPage = lazy(() => import('./pages/admin/AdminCameraFleetPage'));
const AdminPlayerFleetPage = lazy(() => import('./pages/admin/AdminPlayerFleetPage'));
const AdminPlayerReleasesPage = lazy(() => import('./pages/admin/AdminPlayerReleasesPage'));
const AdminLiveViewSettingsPage = lazy(() => import('./pages/admin/AdminLiveViewSettingsPage'));
const InstallPlayerPage = lazy(() => import('./pages/help/InstallPlayerPage'));
const AdminLeadsPage = lazy(() => import('./pages/admin/AdminLeadsPage'));
const AdminAuditLogPage = lazy(() => import('./pages/admin/AdminAuditLogPage'));
const VerifyScreenPage = lazy(() => import('./pages/verification/VerifyScreenPage'));
const ClaimPlayerQrPage = lazy(() => import('./pages/player-pairing/ClaimPlayerQrPage'));
const AdminCreativeReviewPage = lazy(() => import('./pages/admin/AdminCreativeReviewPage'));
const MediaLibraryPage = lazy(() => import('./pages/media/MediaLibraryPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
// The self-managed CMS app is hidden for now: owners choose Private or Public in the main app.

const LedZoneConfigPage = lazy(() => import('./pages/screens/LedZoneConfigPage'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Never retry client (4xx) errors — they won't succeed on retry
        if (isAxiosError(error)) {
          const status = error.response?.status;
          if (status !== undefined && status >= 400 && status < 500) {
            return false;
          }
        }
        // Retry network / 5xx errors up to 2 times
        return failureCount < 2;
      },
    },
  },
});

const SuspenseFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
    }}
  >
    <CircularProgress />
  </Box>
);

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const accessToken = useAuthStore((state) => state.accessToken);
  const logout = useAuthStore((state) => state.logout);
  // After signing in, come back here: a player QR scanned on a phone that was signed out must still reach its
  // pairing page with the token (LoginPage honours ?returnUrl= for same-site paths).
  const location = useLocation();
  const loginPath = `/login?returnUrl=${encodeURIComponent(location.pathname + location.search)}`;

  // The refresh token lives in an HttpOnly cookie now — JavaScript can't read
  // it to decide "is a refresh possible" up front. So: a valid access token
  // means usable immediately; an expired one means we don't know yet and
  // must ask the server (a silent refresh call, which succeeds only if the
  // browser still holds a valid refresh cookie).
  const [checking, setChecking] = useState(() => isAuthenticated && isTokenExpired(accessToken));

  useEffect(() => {
    if (!isAuthenticated) {
      setChecking(false);
      return;
    }
    if (!isTokenExpired(accessToken)) {
      setChecking(false);
      return;
    }

    let cancelled = false;
    setChecking(true);
    doRefresh()
      .catch(() => {
        if (!cancelled) logout();
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });

    return () => {
      cancelled = true;
    };
    // Only re-run when auth identity or the specific token value changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }
  if (checking) {
    return <SuspenseFallback />;
  }
  if (isTokenExpired(accessToken)) {
    return <Navigate to={loginPath} replace />;
  }
  return <>{children}</>;
};

// Legacy /screens/:id/pricing[/festive] links land on the Slots & Pricing tab
const PricingTabRedirect: React.FC = () => {
  const { id } = useParams();
  return <Navigate to={`/screens/${id}?tab=slots-pricing`} replace />;
};

// Shows landing page for guests; redirects authenticated users into the app
const SmartRoot: React.FC = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  return isAuthenticated ? <Navigate to="/dashboard" replace /> : <LandingPage />;
};

// Component that initializes global handlers (must be inside SnackbarProvider)
const AppGlobalHandlers: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  useRateLimitHandler();
  return (
    <>
      <OfflineBanner variant="snackbar" />
      {children}
    </>
  );
};

function App() {
  return (
    <Sentry.ErrorBoundary fallback={<ErrorBoundary><div /></ErrorBoundary>}>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
        <ThemeModeProvider>
          <SnackbarProvider maxSnack={3}>
            <AppGlobalHandlers>
              <BrowserRouter>
                <Suspense fallback={<SuspenseFallback />}>
                <Routes>
                {/* Landing page — public for guests, redirects to dashboard if authenticated */}
                <Route path="/" element={<SmartRoot />} />
                {/* Public Routes */}
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/register/private" element={<RegisterPage mode="private" />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/verify-phone" element={<VerifyPhonePage />} />
                <Route path="/resend-verification" element={<ResendVerificationPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/demo" element={<DemoPage />} />
                <Route path="/explore" element={<ExploreScreensPage />} />
                <Route path="/verify/:screenId" element={<VerifyScreenPage />} />
                <Route path="/about" element={<AboutUs />} />
                <Route path="/contact" element={<ContactUs />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/refund" element={<RefundPolicy />} />
                <Route path="/cookies" element={<CookiesPolicy />} />
                <Route path="/content-policy" element={<ContentPolicy />} />
                <Route path="/community-guidelines" element={<CommunityGuidelines />} />
                <Route path="/disclaimer" element={<Disclaimer />} />
                <Route
                  path="/"
                  element={
                    <ProtectedRoute>
                      <MainLayout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="dashboard" element={<DashboardPage />} />
                  <Route path="campaigns" element={<CampaignsPage />} />
                  <Route path="campaigns/new" element={<CreateCampaignPage />} />
                  <Route path="campaigns/create" element={<Navigate to="/campaigns/new" replace />} />
                  <Route path="campaigns/:id/edit" element={<EditCampaignPage />} />
                  <Route path="campaigns/:id" element={<CampaignDetailPage />} />
                  <Route path="campaigns/:id/add" element={<AddScreensPage />} />
                  <Route path="campaigns/:id/creatives/new" element={<CampaignCreativeUploadPage />} />
                  {/* /media/... is the video store on the host's nginx, so a reload of /media would 404 there. */}
                  <Route path="library" element={<MediaLibraryPage />} />
                  <Route path="media" element={<Navigate to="/library" replace />} />
                  <Route path="screens" element={<ScreensPage />} />
                  <Route path="screens/new" element={<CreateScreenPage />} />
                  <Route path="screens/discover" element={<DiscoverScreensPage />} />
                  <Route path="screens/:id/edit" element={<UpdateScreenPage />} />
                  <Route path="screens/:id" element={<ScreenDetailPage />} />
                  {/* Pricing now lives in the screen console's Slots & Pricing tab; old links redirect */}
                  <Route path="screens/:id/pricing" element={<PricingTabRedirect />} />
                  <Route path="screens/:id/pricing/festive" element={<PricingTabRedirect />} />
                  <Route path="bookings" element={<RoleAccessGate rule="bookings"><BookingsPage /></RoleAccessGate>} />
                  {/* The old single-booking form is gone; its links open the wizard or the campaign */}
                  <Route path="bookings/new" element={<LegacyBookingRedirect />} />
                  <Route path="bookings/:id" element={<RoleAccessGate rule="bookings"><BookingDetailPage /></RoleAccessGate>} />
                  <Route path="bookings/:id/receipt" element={<RoleAccessGate rule="bookings"><BookingReceiptPage /></RoleAccessGate>} />
                  <Route path="reports/bookings/:bookingId" element={<AdvertiserReportPage />} />
                  <Route path="reports/campaigns/:campaignId" element={<CampaignReportPage />} />
                  <Route path="analytics" element={<AnalyticsPage />} />
                  <Route path="money" element={<RoleAccessGate rule="payouts"><MoneyPage /></RoleAccessGate>} />
                  <Route path="wallet" element={<WalletPage />} />
                  <Route path="payouts" element={<RoleAccessGate rule="payouts"><PayoutsPage /></RoleAccessGate>} />
                  {/* payout notifications sent before 27 Oct 2026 link to /payouts/<id> */}
                  <Route path="payouts/:id" element={<Navigate to="/money?tab=payouts" replace />} />
                  <Route path="logs" element={<RoleAccessGate rule="vision"><LogsPage /></RoleAccessGate>} />
                  {/* The monitoring room: every campaign and booked screen, live (advertisers included) */}
                  <Route path="monitor" element={<MonitorRoomPage />} />
                  <Route path="vision/log" element={<Navigate to="/logs?tab=camera" replace />} />
                  <Route path="notifications" element={<NotificationsPage />} />
                  <Route path="notifications/settings" element={<Navigate to="/notifications" replace />} />
                  <Route path="profile" element={<ProfileSettingsPage />} />
                  <Route path="player-pair" element={<ClaimPlayerQrPage />} />
                  <Route path="help/install-player" element={<RoleAccessGate rule="player-setup"><InstallPlayerPage /></RoleAccessGate>} />
                  <Route path="admin/payouts" element={<RoleAccessGate rule="admin"><AdminPayoutsPage /></RoleAccessGate>} />
                  <Route path="admin/machines" element={<RoleAccessGate rule="admin"><AdminMachinesPage /></RoleAccessGate>} />
                  <Route path="admin/verifications" element={<RoleAccessGate rule="admin"><AdminVerificationsPage /></RoleAccessGate>} />
                  <Route path="admin/visibility-requests" element={<RoleAccessGate rule="admin"><AdminVisibilityRequestsPage /></RoleAccessGate>} />
                  <Route path="admin/vision-requests" element={<RoleAccessGate rule="admin"><AdminVisionRequestsPage /></RoleAccessGate>} />
                  <Route path="admin/camera-fleet" element={<RoleAccessGate rule="admin"><AdminCameraFleetPage /></RoleAccessGate>} />
                  <Route path="admin/player-fleet" element={<RoleAccessGate rule="admin"><AdminPlayerFleetPage /></RoleAccessGate>} />
                  <Route path="admin/player-releases" element={<RoleAccessGate rule="admin"><AdminPlayerReleasesPage /></RoleAccessGate>} />
                  <Route path="admin/live-view" element={<RoleAccessGate rule="admin"><AdminLiveViewSettingsPage /></RoleAccessGate>} />
                  <Route path="admin/leads" element={<RoleAccessGate rule="admin"><AdminLeadsPage /></RoleAccessGate>} />
                  <Route path="admin/audit" element={<RoleAccessGate rule="admin"><AdminAuditLogPage /></RoleAccessGate>} />
                  <Route path="admin/creatives/review" element={<RoleAccessGate rule="admin"><AdminCreativeReviewPage /></RoleAccessGate>} />
                  <Route path="screens/:screenId/led-zones" element={<LedZoneConfigPage />} />
                </Route>

                {/* The CMS app is hidden for now; old links land on the dashboard */}
                <Route path="/cms/*" element={<Navigate to="/dashboard" replace />} />

                {/* 404 catch-all */}
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
                </Suspense>
              </BrowserRouter>
            </AppGlobalHandlers>
          </SnackbarProvider>
        </ThemeModeProvider>
      </QueryClientProvider>
    </ErrorBoundary>
    </Sentry.ErrorBoundary>
  );
}

export default App;
